import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { defer, of as observableOf, Subject } from 'rxjs'
import { catchError, exhaustMap } from 'rxjs/operators'
import { resetComposer, updatePost } from 'services/compose'
import { composerPostEditSelector } from 'services/compose/selectors'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { MediaItemState } from 'services/compose/state'

export function useUpdatePost(files: Record<string, MediaItemState>) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()
  const postEdit = useSelector(composerPostEditSelector)
  const [isUpdating, setIsUpdating] = useState(false)
  const update$ = useRef<Subject<void>>(undefined)

  const handleResponse = useCallback((response: any) => {
    if (response.error) {
      const serverMessage = response.error.response?.error?.message
      const failedMessage = intl.formatMessage({ id: 'notifications.post-update-failed' })
      dispatch(message(serverMessage ? `${serverMessage}. ${failedMessage}` : failedMessage, 'error'))
    } else {
      dispatch(message(intl.formatMessage({ id: 'notifications.post-updated' }), 'success'))
      const locationState = location.state as any || {}
      const opener = locationState.opener
      const backUrl = opener && opener !== '/composer' ? opener : '/posts'
      const timestamp = Date.now().toString()
      navigate(backUrl, {
        state: {
          refresh: timestamp,
          search: locationState.search,
          ppid: postEdit?.ppid,
          updatedPostHash: postEdit?.id ? `${postEdit?.id}-${timestamp}` : undefined
        }
      })
      dispatch(resetComposer())
    }
    setIsUpdating(false)
  }, [dispatch, navigate, intl, location.state, postEdit?.ppid, postEdit?.id])

  // The update pipeline must survive re-renders: recreating the Subject would drop the exhaustMap
  // in-flight guard and allow concurrent updates. Changing values are read through refs instead.
  const filesRef = useRef(files)
  const handleResponseRef = useRef(handleResponse)

  useEffect(() => {
    filesRef.current = files
    handleResponseRef.current = handleResponse
  })

  useEffect(() => {
    const subject = new Subject<void>()
    update$.current = subject
    subject
      .pipe(
        exhaustMap(() => {
          setIsUpdating(true)
          // Contain any error thrown while building or running the request, so the pipeline stays alive.
          return defer(() => dispatch(updatePost(filesRef.current as any))).pipe(
            catchError((error: any) => observableOf({ error }))
          )
        })
      )
      .subscribe({
        next: (response: any) => handleResponseRef.current(response),
        // Last resort: the pipeline is dead at this point, at least surface the failure.
        error: () => {
          setIsUpdating(false)
          dispatch(message(intl.formatMessage({ id: 'notifications.post-update-failed' }), 'error'))
        }
      })

    return () => {
      // Complete source stream so active update requests can finish after composer unmount.
      subject.complete()
    }
  }, [dispatch, intl])

  const saveEdit = useCallback(() => {
    update$.current?.next()
  }, [])

  return { saveEdit, isUpdating }
}
